import { useEffect, useState } from 'react';
import { RouterProvider, useNav } from './lib/router.js';
import { StoreProvider, featureOn, useStore } from './lib/store.js';
import { Notif, Toast } from './components/ui.js';
import { TabBar } from './components/TabBar.js';
import { Home } from './screens/Home.js';
import { Menu } from './screens/Menu.js';
import { Build } from './screens/Build.js';
import { Bag } from './screens/Bag.js';
import { Status } from './screens/Status.js';
import { CardBack } from './screens/CardBack.js';
import { ActivityPage } from './components/Activity.js';
import { Account, AccountPage } from './screens/Account.js';
import { Join } from './screens/Join.js';
import { Invite, PayReturn, Soon } from './screens/Misc.js';
import { CreatorHubPage } from './screens/Creator.js';
import { Messages } from './screens/Messages.js';
import { Catering } from './screens/Catering.js';
import { CateringHelp, CateringOrders } from './screens/CateringOrders.js';
import { CateringOrder } from './screens/CateringOrder.js';
import { TeamPick } from './screens/TeamPick.js';
import { GroupStart } from './screens/Group.js';
import { GroupLink } from './screens/GroupLink.js';
import { LegalIndex, LegalPage } from './screens/Legal.js';
import { CreditSheet, DeleteSheet, NameSheet, PayingSheet, PickRewardSheet, PickupSheet, SaveOrderSheet, SpinRulesSheet, TopupSheet, EmailListSheet } from './components/Sheets.js';
import { CollectFx, PlacedFx } from './components/Fx.js';
import { CardMenuSheet, EarnSheet, RewardsSheet } from './components/HomeSheets.js';
import { CodeSheet } from './components/CodeSheet.js';
import { NotificationsSheet } from './components/NotificationsAsk.js';
import { markJustJoined } from './lib/notifications.js';
import { bagCount } from './lib/bag.js';
import { testToolsAllowed } from './lib/platform.js';
import { NativeShell } from './lib/native.js';
import { LookProvider, useLook } from './lib/look.js';
import { startPath } from './lib/appearance.js';
import { Intro } from './components/Intro.js';
import { ErrorBoundary, ScreenCrashed } from './components/ErrorBoundary.js';

function useLaptop(): boolean {
  const [laptop, setLaptop] = useState(() => window.matchMedia('(min-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const on = () => setLaptop(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-view', laptop ? 'laptop' : 'phone');
  }, [laptop]);
  return laptop;
}

function Screen({ laptop }: { laptop: boolean }) {
  const { screen, navigate } = useNav();
  const s = useStore();
  // Home switched off as a tab in the admin: the app opens on the first tab instead.
  const start = startPath(useLook().v, featureOn(s, 'catering'));
  useEffect(() => {
    if (screen === 'home' && start !== '/') navigate(start, { root: true, replace: true });
  }, [screen, start]); // eslint-disable-line react-hooks/exhaustive-deps
  // The View bag bar is up while the bag has items: the Order tab's page keeps room under it too (styles.css).
  useEffect(() => {
    document.documentElement.toggleAttribute('data-bag', bagCount(s.bag) > 0);
  }, [s.bag]);
  useEffect(() => {
    if (testToolsAllowed(s.config?.testTools)) document.documentElement.setAttribute('data-testmode', '1');
    else document.documentElement.removeAttribute('data-testmode');
  }, [s.config?.testTools]);
  if (s.bootError) return <div className="scroll"><div className="pad"><h1 className="display">Tikkas</h1><p className="err">{s.bootError}</p><button type="button" className="yellow" onClick={() => location.reload()}>Try again</button></div></div>;
  switch (screen) {
    case 'home': return <Home />;
    case 'menu': return <Menu laptop={laptop} />;
    case 'build': return <Build />;
    case 'bag': return <Bag />;
    case 'status': return <Status />;
    case 'cardback': return <CardBack />;
    case 'activity': return <ActivityPage />;
    case 'account': return <Account />;
    case 'accountpage': return <AccountPage />;
    case 'join': return <Join />;
    case 'invite': return <Invite />;
    case 'creator': return <CreatorHubPage />;
    case 'messages': return <Messages />;
    case 'payreturn': return <PayReturn />;
    // Switchable features (Admin → Settings → Features): off means the server refuses them too, so the page just says so.
    case 'cater': return featureOn(s, 'catering') ? <Catering /> : <Soon what="Office catering" blurb="Lunch for the team, delivered by cargo bike. Opening soon." />;
    case 'corders': return featureOn(s, 'catering') ? <CateringOrders /> : <Soon what="Office catering" blurb="Lunch for the team, delivered by cargo bike. Opening soon." />;
    case 'chelp': return featureOn(s, 'catering') ? <CateringHelp /> : <Soon what="Office catering" blurb="Lunch for the team, delivered by cargo bike. Opening soon." />;
    case 'corder': return featureOn(s, 'catering') ? <CateringOrder /> : <Soon what="Office catering" blurb="Lunch for the team, delivered by cargo bike. Opening soon." />;
    case 'teampick': return featureOn(s, 'catering') ? <TeamPick /> : <Soon what="Office catering" blurb="Lunch for the team, delivered by cargo bike. Opening soon." />;
    case 'legal': return <LegalIndex />;
    case 'legaldoc': return <LegalPage />;
    case 'group': return featureOn(s, 'groupOrder') ? <GroupStart /> : <Soon what="Group order" blurb="One link for your friends, one pickup, everyone adds their own. Opening soon." />;
    case 'grouplink': return featureOn(s, 'groupOrder') ? <GroupLink /> : <Soon what="Group order" blurb="Group orders aren't available just now." />;
    default: return <Home />;
  }
}

/** The screen. Tabs switch at once (Saif, 22 Oct: the page slide went; a 120 ms fade on the incoming page, nothing under Reduce Motion). The WebView's left-edge back gesture stays. */
function ScreenHost({ laptop }: { laptop: boolean }) {
  const { screen, route, navigate } = useNav();
  // One broken screen never blanks the app: the tab bar and Show code stay, and moving to another page clears it.
  return (
    <main id="screen" data-screen={screen}>
      <ErrorBoundary where={`screen:${screen}`} resetKey={route.path} fallback={(reset) => <ScreenCrashed reset={reset} home={() => navigate('/', { root: true })} />}>
        <Screen laptop={laptop} />
      </ErrorBoundary>
    </main>
  );
}

function Overlays() {
  const s = useStore();
  const sheet = s.sheet;
  const fx = s.fx;
  // A sheet that breaks closes, with a toast, instead of taking the page with it.
  return (
    <ErrorBoundary where={`sheet:${sheet?.kind ?? fx?.kind ?? 'none'}`} resetKey={`${sheet?.kind ?? ''}|${fx?.kind ?? ''}`} onError={() => { s.closeSheet(); s.setFx(null); s.toast("That didn't open. Please try again."); }}>
      <OverlaysInner />
    </ErrorBoundary>
  );
}

function OverlaysInner() {
  const s = useStore();
  const sheet = s.sheet;
  const fx = s.fx;
  return (
    <>
      {sheet?.kind === 'pickup' && <PickupSheet />}
      {sheet?.kind === 'topup' && <TopupSheet amountPence={sheet.amountPence} />}
      {sheet?.kind === 'credit' && <CreditSheet />}
      {sheet?.kind === 'pickMain' && <PickRewardSheet reward={sheet.reward} mainsOnly dessertsOnly={false} />}
      {sheet?.kind === 'pickSide' && <PickRewardSheet reward={sheet.reward} mainsOnly={false} dessertsOnly={sheet.dessertsOnly} />}
      {sheet?.kind === 'delete' && <DeleteSheet />}
      {sheet?.kind === 'saveOrder' && (s.me?.liveOrders.find((o) => o.id === sheet.orderId) ?? (s.me?.lastOrder?.id === sheet.orderId ? s.me.lastOrder : null)) && <SaveOrderSheet order={(s.me?.liveOrders.find((o) => o.id === sheet.orderId) ?? s.me?.lastOrder)!} />}
      {sheet?.kind === 'spinRules' && <SpinRulesSheet />}
      {sheet?.kind === 'paying' && <PayingSheet label={sheet.label} />}
      {/* Joining ends with the name sheet: the first Home then asks about notifications (lib/notifications.ts). */}
      {sheet?.kind === 'name' && <NameSheet onDone={() => { if (sheet.welcome) markJustJoined(); s.closeSheet(); }} welcome={!!sheet.welcome} />}
      {sheet?.kind === 'notifications' && s.me && <NotificationsSheet mode={sheet.mode} place={sheet.place} />}
      {sheet?.kind === 'emailList' && s.me && <EmailListSheet />}
      {sheet?.kind === 'cardMenu' && <CardMenuSheet />}
      {sheet?.kind === 'rewards' && <RewardsSheet />}
      {sheet?.kind === 'earn' && <EarnSheet />}
      {sheet?.kind === 'code' && s.me && <CodeSheet onClose={s.closeSheet} />}
      {fx?.kind === 'placed' && <PlacedFx order={fx.order} onDone={() => s.setFx(null)} />}
      {fx?.kind === 'collected' && <CollectFx from={fx.from} to={fx.to} earn={fx.earn} spinBonus={fx.spinBonus} number={fx.number} unlocked={fx.unlocked} onDone={() => s.setFx(null)} />}
      <Toast />
      <Notif />
    </>
  );
}

export function App() {
  const laptop = useLaptop();
  return (
    <RouterProvider>
      <StoreProvider>
        <LookProvider>
          <div id="app">
            <ScreenHost laptop={laptop} />
            <TabBar laptop={laptop} />
            <Overlays />
            <NativeShell />
            <Intro />
          </div>
        </LookProvider>
      </StoreProvider>
    </RouterProvider>
  );
}
